import { type VendorCatalogProduct } from '@choco-admin/types';
import { Avatar, Chip, TableCell, TableRow } from '@heroui/react';
import React from 'react';
import { FiPackage } from 'react-icons/fi';

import {
  LoadingTable,
  type LoadingTableColumn,
} from '../../../../../../../components/Table';
import { resolveCatalogStatusChip } from '../utils/catalogStatusChip';
import { formatVendorCatalogPrice } from '../utils/vendorCatalogMapper';

interface CatalogProductTableProps {
  products: VendorCatalogProduct[];
  isLoading: boolean;
  /** Opens the read-only product detail view for the clicked row. */
  onSelectProduct: (product: VendorCatalogProduct) => void;
}

const columns: LoadingTableColumn[] = [
  { label: 'Product', className: 'pl-6 min-w-64' },
  { label: 'Product code', className: 'hidden md:table-cell min-w-32' },
  { label: 'Unit', className: 'hidden lg:table-cell min-w-28' },
  { label: 'Cost', className: 'min-w-28' },
  { label: 'Status', className: 'min-w-32' },
];

export const CatalogProductTable: React.FC<CatalogProductTableProps> = ({
  products,
  isLoading,
  onSelectProduct,
}) => {
  const renderRow = (product: VendorCatalogProduct) => {
    const status = resolveCatalogStatusChip(product);

    return (
      <TableRow
        key={product.id}
        className="cursor-pointer transition-colors hover:bg-default-50 focus-visible:outline-2 focus-visible:outline-primary"
        tabIndex={0}
        aria-label={`View details for ${product.name}`}
        onClick={() => onSelectProduct(product)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            // Space would otherwise scroll the page; Enter is a no-op default.
            event.preventDefault();
            onSelectProduct(product);
          }
        }}
      >
        <TableCell className="pl-6">
          <div className="flex items-center gap-3">
            <Avatar
              src={product.imageUrl ?? undefined}
              radius="sm"
              size="md"
              fallback={<FiPackage className="text-default-400" size={18} />}
              className="flex-shrink-0 bg-default-100"
              aria-hidden
            />
            <span className="font-medium text-foreground">{product.name}</span>
          </div>
        </TableCell>
        <TableCell className="hidden md:table-cell">
          <span
            className="font-mono text-xs text-default-600 select-text cursor-text"
            onClick={(event) => event.stopPropagation()}
          >
            {product.productCode ?? '—'}
          </span>
        </TableCell>
        <TableCell className="hidden lg:table-cell">
          <span className="text-sm text-default-600">
            {product.unit || '—'}
          </span>
        </TableCell>
        <TableCell>
          <span className="text-sm text-foreground">
            {formatVendorCatalogPrice(product.price)}
          </span>
        </TableCell>
        <TableCell>
          <Chip size="sm" variant="flat" color={status.color}>
            {status.label}
          </Chip>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <LoadingTable
      columns={columns}
      data={products}
      renderRow={renderRow}
      isLoading={isLoading}
      isEmpty={products.length === 0}
      rowCount={5}
      ariaLabel="Catalog products table"
      loadingMessage="Loading catalog products..."
      emptyState={{
        title: 'No products found',
        description:
          'This distributor has no catalog products matching the current filters.',
        icon: FiPackage,
      }}
    />
  );
};
